import { subject } from '@casl/ability';
import {
    assertUnreachable,
    type ResourceViewItemType,
    type SpaceSummary,
} from '@lightdash/common';
import { Paper, Stack, TextInput } from '@mantine-8/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useMemo, useState } from 'react';
import useApp from '../../../providers/App/useApp';
import AdminContentViewFilter from '../ResourceView/AdminContentViewFilter';
import Tree from '../Tree/Tree';
import { type NestableItem } from '../Tree/types';
import useFuzzyTreeSearch from '../Tree/useFuzzyTreeSearch';
import styles from './SpaceSelector.module.css';

type SpaceSelectorProps = {
    projectUuid: string | undefined;
    selectedSpaceUuid: string | null;
    spaces:
        | Array<
              Pick<
                  SpaceSummary,
                  'inheritsFromOrgOrProject' | 'access' | 'userAccess'
              > &
                  NestableItem
          >
        | undefined;
    isLoading?: boolean;
    itemType: ResourceViewItemType | undefined;
    onSelectSpace: (spaceUuid: string | null) => void;
    isRootSelectionEnabled?: boolean;
};

const SpaceSelector = ({
    projectUuid,
    selectedSpaceUuid,
    spaces = [],
    isLoading: _isLoading, // TODO: implement loading state for the tree.
    onSelectSpace,
    children,
    isRootSelectionEnabled,
}: React.PropsWithChildren<SpaceSelectorProps>) => {
    const { user } = useApp();

    const userCanManageProject = user.data?.ability?.can(
        'manage',
        subject('Project', {
            organizationUuid: user.data?.organizationUuid,
            projectUuid: projectUuid,
        }),
    );

    const [selectedAdminContentType, setSelectedAdminContentType] = useState<
        'all' | 'shared'
    >(userCanManageProject ? 'shared' : 'all');

    const filteredSpaces = useMemo(() => {
        if (!user.data) return [];

        switch (selectedAdminContentType) {
            case 'all':
                return spaces;
            case 'shared': {
                // Instead of dropping inaccessible spaces, keep them in the
                // tree as non-selectable placeholders so hierarchy is preserved
                // and same-named children remain visually distinguishable.
                return spaces.map((space) => {
                    const isAccessible = space.userAccess;
                    if (isAccessible) return space;
                    return { ...space, restricted: true };
                });
            }
            default:
                return assertUnreachable(
                    selectedAdminContentType,
                    `Invalid admin content type when filtering spaces: ${selectedAdminContentType}`,
                );
        }
    }, [user.data, selectedAdminContentType, spaces]);

    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 200);

    const fuzzyFilteredSpaces = useFuzzyTreeSearch(
        filteredSpaces,
        debouncedSearchQuery,
    );

    return (
        <Stack>
            {userCanManageProject ? (
                <AdminContentViewFilter
                    value={selectedAdminContentType}
                    onChange={setSelectedAdminContentType}
                    withDivider={false}
                    segmentedControlProps={{
                        flex: '0 0 auto',
                    }}
                />
            ) : null}

            <TextInput
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search spaces"
            />

            <Paper w="100%" h={400} className={styles.treeContainer} withBorder>
                <Tree
                    withRootSelectable={isRootSelectionEnabled}
                    data={fuzzyFilteredSpaces ?? filteredSpaces}
                    value={selectedSpaceUuid}
                    onChange={onSelectSpace}
                    topLevelLabel="Spaces"
                    isExpanded={fuzzyFilteredSpaces !== undefined}
                    type="single"
                />
            </Paper>

            {children}
        </Stack>
    );
};

export default SpaceSelector;
